import classnames from 'classnames';
import React, { useMemo, useRef } from 'react';
import Skeleton from 'react-loading-skeleton';

import { formatAccountNumber, truncateName } from '@eika-infrastruktur/format';
import { AttentionLabel, Label } from '@eika/label';

import type { EFaktura } from '../../../generated-types';
import useTransactionActor from '../../../hooks/useTransactionActor';
import useTranslation, { format } from '../../../hooks/useTranslation';
import { currencyFormatter } from '../../shared/numberFormat';
import EFakturaItemSkeleton from './EFakturaItemSkeleton';
import { getDueDateLabel, getDueDateLabelTypeAndIcon } from './getDueDateLabel';
import useIsDue from './useIsDue';

import './EFakturaItem.scss';

interface Props {
    // @default true
    showPaymentTypeLabel: boolean;
    eFaktura: EFaktura;
    openModal: (efaktura: EFaktura) => void;
    alternateLook?: boolean;
    // @default false;
    isLoading?: boolean;
}

const EFakturaItem: React.FC<Props> = ({
    eFaktura,
    openModal,
    alternateLook = false,
    showPaymentTypeLabel = true,
    isLoading = false,
}) => {
    const toAccount = useRef({
        accountNumber: eFaktura.toAccountNumber,
        name: eFaktura.issuerName,
    });

    const translated = useTranslation();

    const labelText = translated.eFakturaItemType[eFaktura.efakturaType];
    const { transactionActor: finalReceiver, isLoading: transactionActorLoading } = useTransactionActor(
        toAccount.current,
    );

    const dueType = useIsDue(eFaktura.paymentDate);
    const generateDueDateText = useMemo(() => getDueDateLabel(translated), [translated]);
    const { type, icon } = getDueDateLabelTypeAndIcon(dueType);

    if (isLoading) return <EFakturaItemSkeleton />;

    return (
        <li
            className={classnames('eFaktura-item', { 'alternate-look': alternateLook })}
            aria-labelledby={`efaktura-issuer-${eFaktura.id.value}`}
            data-testid="eFaktura-item"
        >
            <button
                type="button"
                onClick={() => openModal(eFaktura)}
                className={classnames(
                    'eFaktura-item__button',
                    { 'due-today': dueType === 'today' },
                    { 'over-due': dueType === 'overdue' },
                )}
            >
                <span className="eFaktura-item__due-date">
                    {showPaymentTypeLabel && <AttentionLabel text={labelText} size="small" />}
                    <Label
                        type={type}
                        icon={icon}
                        className="due-date__info"
                        text={generateDueDateText(dueType, eFaktura.paymentDate)}
                        size="small"
                    />
                    {eFaktura?.owner && (
                        <Label
                            size="small"
                            text={format(translated.approvalItem.sharedBy, truncateName(eFaktura.owner.name))}
                        />
                    )}
                </span>

                <span className="eFaktura-item__receiver-and-amount">
                    {transactionActorLoading ? (
                        <span className="eFaktura-item__receiver-name">
                            <Skeleton height={20} width={115} />
                        </span>
                    ) : (
                        <p id={`efaktura-issuer-${eFaktura.id.value}`} className="eFaktura-item__receiver-name">
                            {finalReceiver?.name ?? formatAccountNumber(finalReceiver?.accountNumber)}
                        </p>
                    )}
                    <span className="eFaktura-item__amount">
                        {currencyFormatter(eFaktura.amount, eFaktura.currency ?? 'NOK')}
                    </span>
                </span>
            </button>
        </li>
    );
};

export default EFakturaItem;
